import { liquidSettleEvent } from '@/play/liquid/changes'
import { liquidKindByColor } from '@/play/liquid/kindByColor'
import { BLOB_MOLTEN_SILVER_COLOR } from '@/voxels/blob_sdf/constants'
import type { VoxelMaterial } from '@/voxelTypes'

/**
 * The liquids, by colour. A blob surface is drawn in the average colour of the connected cells of
 * one material (src/voxels/blob_sdf: one surface per (type, material) group, one colour per
 * connected component), so two colours of one material that meet turn into one blended colour over
 * the whole pool (007's gold vanished into silver). So every liquid here has its own material and
 * keeps its own surface: liquid metal (metallic), pink goo (glossy, the default), lime goo (shiny),
 * blue goo (soft). One metal colour only, for the same reason.
 */
export const OOZE_METAL = BLOB_MOLTEN_SILVER_COLOR
export const OOZE_PINK = 0xff5fa2
export const OOZE_LIME = 0x7ee35b
export const OOZE_BLUE = 0x3fb8ff
export const OOZE_GOO_COLORS = [OOZE_PINK, OOZE_LIME, OOZE_BLUE] as const

const MATERIAL = new Map<number, VoxelMaterial | undefined>([
  [OOZE_METAL, 'metallic'],
  [OOZE_PINK, undefined],
  [OOZE_LIME, 'shiny'],
  [OOZE_BLUE, 'soft'],
])

/**
 * How heavy each liquid is (heavier sinks through lighter, lighter rises). Metal is three times
 * as heavy as any goo: it always ends up at the bottom. The goos differ a little (blue under lime
 * under pink), so where they meet they settle into bands; at equal density they never displace
 * each other and interleave cell by cell where two streams cross, which, with a surface per
 * material, draws as freckles of one goo inside the other.
 */
const DENSITY = new Map<number, number>([
  [OOZE_METAL, 3],
  [OOZE_BLUE, 1.2],
  [OOZE_LIME, 1.1],
  [OOZE_PINK, 1],
])

export const isOozeMetal = (color: number) => (color >>> 0) === OOZE_METAL
export const oozeDensityOf = (color: number) => DENSITY.get(color >>> 0) ?? 1
export const oozeMaterialOf = (color: number) => MATERIAL.get(color >>> 0)

/** Every blob in an Ooze world flows, in one sim (so they displace each other); a moved cell keeps its material. */
export const OOZE_LIQUID = liquidKindByColor('blob', oozeMaterialOf)

/** One frame of the flow: the change set of what moved. Joins the dig's undo step. */
export const OOZE_FLOW = liquidSettleEvent('experience.ooze.flow', OOZE_LIQUID)

/** Reducer handlers for Ooze (`experience.ooze.*`). */
export const eventHandlers = OOZE_FLOW.handlers
